// SPDX-License-Identifier: Apache-2.0
// File: src/presets/FactorySoundLibrary.ts
import type { SynthParamId } from '../ui/theme/PocketGridRetroTheme';

export type InstrumentCategoryId =
  | 'KEYS_PIANOS'
  | 'BASSES'
  | 'LEADS_BRASS'
  | 'PADS_STRINGS'
  | 'BELLS_MALLETS'
  | 'PHYSICAL_DRUMS'
  | 'VOCODER_CHOIR'
  | 'IMPORTED_SYX';

export interface InstrumentCategoryInfo {
  id: InstrumentCategoryId;
  label: string;
  shortLabel: string;
  icon: string;
  initTemplateName: string;
}

export const INSTRUMENT_CATEGORIES: InstrumentCategoryInfo[] = [
  { id: 'KEYS_PIANOS', label: 'KEYS & PIANOS', shortLabel: 'Keys & Piano', icon: '🎹', initTemplateName: 'Init Keys & Piano' },
  { id: 'BASSES', label: 'BASSES', shortLabel: 'Bass', icon: '🎸', initTemplateName: 'Init Studio Bass' },
  { id: 'LEADS_BRASS', label: 'LEADS & BRASS', shortLabel: 'Lead & Brass', icon: '🎺', initTemplateName: 'Init Synth Lead' },
  { id: 'PADS_STRINGS', label: 'PADS & STRINGS', shortLabel: 'Pad & Strings', icon: '🎻', initTemplateName: 'Init Lush Pad' },
  { id: 'BELLS_MALLETS', label: 'BELLS & MALLETS', shortLabel: 'Bell & Mallet', icon: '🔔', initTemplateName: 'Init Bell & Mallet' },
  { id: 'PHYSICAL_DRUMS', label: 'PHYSICAL DRUMS', shortLabel: 'Drum Kit', icon: '🥁', initTemplateName: 'Init Physical Drum' },
  { id: 'VOCODER_CHOIR', label: 'VOCODER & CHOIR', shortLabel: 'Vocoder Choir', icon: '🗣', initTemplateName: 'Init Vocoder Choir' },
  { id: 'IMPORTED_SYX', label: 'IMPORTED .SYX', shortLabel: 'DX7 .SYX', icon: '📄', initTemplateName: 'Init SYX Patch' },
];

// The 16 Most Popularly Customized Synth Parameters shown underneath the Sound Bank
export const SIXTEEN_CUSTOMIZER_PARAM_IDS: SynthParamId[] = [
  'algMorph',
  'waveFold',
  'fmFeedback',
  'formantShift',
  'vowelMorphX',
  'vowelMorphY',
  'modAttack',
  'modDecay',
  'carAttack',
  'carDecay',
  'chorusDelayMix',
  'spaceReverbMix',
  'resonatorDecay',
  'drumDispersion',
  'wowFlutter',
  'limiterDrive',
];

export interface SynthPreset {
  id: string;
  name: string;
  category: InstrumentCategoryId;
  engineBadge: '6-OP FM' | 'WAVEGUIDE' | 'VOCODER / FM' | 'PHYSICAL MODEL' | 'USER CUSTOM';
  featureBadge: string;
  tags: string[];
  favorite: boolean;
  isUserSlot?: boolean;
  userSlotIndex?: number; // 1..4 within its category
  isPopulatedUserSlot?: boolean;
  isSyx?: boolean;
  targetEngineId: 0 | 1 | 2; // 0 = FM, 1 = Drums, 2 = Vocoder/Formant
  opRatios: [number, number, number, number, number, number];
  opLevels: [number, number, number, number, number, number];
  params: Partial<Record<SynthParamId | string, number>>;
}

// Helper to build the 4 User-Created Slots (Slots 9..12) for each category with a clean starting template
function createCategoryUserSlots(
  category: InstrumentCategoryId,
  baseTemplate: Omit<SynthPreset, 'id' | 'name' | 'category' | 'isUserSlot' | 'userSlotIndex' | 'isPopulatedUserSlot'>
): SynthPreset[] {
  const catMeta = INSTRUMENT_CATEGORIES.find((c) => c.id === category)!;
  return [1, 2, 3, 4].map((slotNum) => ({
    ...baseTemplate,
    id: `user-slot-${category.toLowerCase()}-${slotNum}`,
    name: `User Slot ${slotNum} (${catMeta.initTemplateName})`,
    category,
    engineBadge: 'USER CUSTOM',
    featureBadge: `USER SLOT ${slotNum}`,
    isUserSlot: true,
    userSlotIndex: slotNum,
    isPopulatedUserSlot: false,
  }));
}

export const FACTORY_PRESETS: SynthPreset[] = [
  // ===========================================================================
  // 1. KEYS & PIANOS — 8 Most Popular Sounds + 4 User Slots
  // ===========================================================================
  {
    id: 'keys-velvet-tine-84',
    name: '1. Velvet Tine EP 84',
    category: 'KEYS_PIANOS',
    engineBadge: '6-OP FM',
    featureBadge: 'CLASSIC RHODES',
    tags: ['Keys', 'Warm', 'Chorus', 'Classic'],
    favorite: true,
    targetEngineId: 0,
    opRatios: [1.0, 14.0, 1.0, 1.0, 3.0, 7.0],
    opLevels: [0.95, 0.36, 0.88, 0.52, 0.25, 0.18],
    params: { algMorph: 0.22, waveFold: 0.12, fmFeedback: 0.28, formantShift: 0.62, modAttack: 0.03, modDecay: 0.58, carAttack: 0.02, carDecay: 0.65, chorusDelayMix: 0.34, spaceReverbMix: 0.24 },
  },
  {
    id: 'keys-wurli-200a-reed',
    name: '2. Vintage Wurli 200A',
    category: 'KEYS_PIANOS',
    engineBadge: 'PHYSICAL MODEL',
    featureBadge: 'REED BITE',
    tags: ['Keys', 'Wurli', 'Soul', 'Warm'],
    favorite: true,
    targetEngineId: 0,
    opRatios: [1.0, 2.0, 1.0, 4.0, 6.0, 8.0],
    opLevels: [0.96, 0.58, 0.86, 0.44, 0.30, 0.22],
    params: { algMorph: 0.30, waveFold: 0.42, fmFeedback: 0.44, formantShift: 0.58, modAttack: 0.01, modDecay: 0.46, carAttack: 0.01, carDecay: 0.55, chorusDelayMix: 0.26, spaceReverbMix: 0.20 },
  },
  {
    id: 'keys-grand-concert-piano',
    name: '3. Studio Acoustic Grand',
    category: 'KEYS_PIANOS',
    engineBadge: 'WAVEGUIDE',
    featureBadge: 'SOUNDBOARD',
    tags: ['Piano', 'Acoustic', 'Hammer', 'Keys'],
    favorite: true,
    targetEngineId: 0,
    opRatios: [1.0, 1.0, 2.0, 3.0, 1.0, 7.0],
    opLevels: [1.00, 0.44, 0.38, 0.22, 0.12, 0.40],
    params: {
      algMorph: 0.06,
      waveFold: 0.02,
      fmFeedback: 0.10,
      formantShift: 0.72,
      modAttack: 0.001,
      modDecay: 0.35,
      carAttack: 0.002,
      carDecay: 0.70,
      chorusDelayMix: 0.04,
      spaceReverbMix: 0.30,
      cutoff: 0.76,
      resonance: 0.16,
      drive: 0.06,
      waveShape: 0.04,
      ampAttack: 0.012,
      ampDecay: 0.68,
      ampSustain: 0.22,
      ampRelease: 0.35,
      fltEnvAmount: 0.72,
      fltDecay: 0.38,
      reverbSpace: 0.30,
      delayMix: 0.06,
    },
  },
  {
    id: 'keys-vintage-clavinet-d6',
    name: '4. Funky Clavinet D6',
    category: 'KEYS_PIANOS',
    engineBadge: 'PHYSICAL MODEL',
    featureBadge: 'FUNK SNAP',
    tags: ['Keys', 'Funky', 'Clav', 'Percussive'],
    favorite: false,
    targetEngineId: 0,
    opRatios: [1.0, 3.0, 2.0, 5.0, 6.0, 8.0],
    opLevels: [0.98, 0.84, 0.76, 0.68, 0.52, 0.40],
    params: { algMorph: 0.15, waveFold: 0.72, fmFeedback: 0.65, formantShift: 0.74, modAttack: 0.01, modDecay: 0.32, carAttack: 0.01, carDecay: 0.28, chorusDelayMix: 0.18, spaceReverbMix: 0.12 },
  },
  {
    id: 'keys-b3-tonewheel-organ',
    name: '5. Warm B3 Drawbar Organ',
    category: 'KEYS_PIANOS',
    engineBadge: '6-OP FM',
    featureBadge: 'ROTARY CHORUS',
    tags: ['Organ', 'Keys', 'Gospel', 'Rotary'],
    favorite: true,
    targetEngineId: 0,
    opRatios: [0.5, 1.0, 1.5, 2.0, 3.0, 4.0],
    opLevels: [0.92, 0.95, 0.70, 0.82, 0.55, 0.48],
    params: { algMorph: 0.88, waveFold: 0.22, fmFeedback: 0.30, formantShift: 0.64, modAttack: 0.01, modDecay: 0.90, carAttack: 0.01, carDecay: 0.92, chorusDelayMix: 0.52, spaceReverbMix: 0.28 },
  },
  {
    id: 'keys-glass-rhodes-morph',
    name: '6. Glass DX Full Tines',
    category: 'KEYS_PIANOS',
    engineBadge: '6-OP FM',
    featureBadge: '80S BALLAD',
    tags: ['Keys', 'DX7', 'Ballad', 'Lush'],
    favorite: false,
    targetEngineId: 0,
    opRatios: [1.0, 7.0, 1.0, 2.0, 11.0, 1.0],
    opLevels: [0.92, 0.44, 0.85, 0.48, 0.22, 0.35],
    params: { algMorph: 0.52, waveFold: 0.15, fmFeedback: 0.22, formantShift: 0.66, modAttack: 0.05, modDecay: 0.72, carAttack: 0.03, carDecay: 0.76, chorusDelayMix: 0.44, spaceReverbMix: 0.38 },
  },
  {
    id: 'keys-cyber-dorian-pluck',
    name: '7. Lo-Fi Tape Felt Piano',
    category: 'KEYS_PIANOS',
    engineBadge: 'WAVEGUIDE',
    featureBadge: 'TAPE WOW',
    tags: ['Keys', 'Lo-Fi', 'Felt', 'Warm'],
    favorite: false,
    targetEngineId: 0,
    opRatios: [1.0, 2.0, 1.0, 3.0, 0.5, 4.0],
    opLevels: [0.95, 0.32, 0.80, 0.20, 0.45, 0.12],
    params: { algMorph: 0.25, waveFold: 0.10, fmFeedback: 0.15, formantShift: 0.40, wowFlutter: 0.55, modAttack: 0.04, modDecay: 0.42, carAttack: 0.03, carDecay: 0.58, chorusDelayMix: 0.36, spaceReverbMix: 0.42 },
  },
  {
    id: 'keys-crystal-harpsichord',
    name: '8. Cyber Pluck Harpsichord',
    category: 'KEYS_PIANOS',
    engineBadge: 'PHYSICAL MODEL',
    featureBadge: 'PLUCK STRING',
    tags: ['Keys', 'Pluck', 'Bright', 'Harpsichord'],
    favorite: false,
    targetEngineId: 0,
    opRatios: [1.0, 2.0, 3.0, 4.0, 6.0, 8.0],
    opLevels: [0.95, 0.78, 0.72, 0.60, 0.45, 0.35],
    params: { algMorph: 0.35, waveFold: 0.64, fmFeedback: 0.48, formantShift: 0.78, modAttack: 0.01, modDecay: 0.28, carAttack: 0.01, carDecay: 0.34, chorusDelayMix: 0.30, spaceReverbMix: 0.26 },
  },
  ...createCategoryUserSlots('KEYS_PIANOS', {
    engineBadge: 'USER CUSTOM',
    featureBadge: 'USER SLOT',
    tags: ['User', 'Keys', 'Custom'],
    favorite: false,
    targetEngineId: 0,
    opRatios: [1.0, 2.0, 1.0, 4.0, 3.0, 7.0],
    opLevels: [0.95, 0.45, 0.85, 0.40, 0.30, 0.20],
    params: { algMorph: 0.28, waveFold: 0.20, fmFeedback: 0.30, formantShift: 0.60, modAttack: 0.03, modDecay: 0.55, carAttack: 0.02, carDecay: 0.60, chorusDelayMix: 0.30, spaceReverbMix: 0.25 },
  }),

  // ===========================================================================
  // 2. BASSES — 8 Most Popular Sounds + 4 User Slots
  // ===========================================================================
  {
    id: 'bass-lately-solid-fm',
    name: '1. Solid 80s Lately Bass',
    category: 'BASSES',
    engineBadge: '6-OP FM',
    featureBadge: 'PUNCH SUB',
    tags: ['Bass', 'Punchy', 'Classic', 'Mono'],
    favorite: true,
    targetEngineId: 0,
    opRatios: [0.5, 1.0, 0.5, 2.0, 3.0, 4.0],
    opLevels: [0.99, 0.86, 0.92, 0.70, 0.48, 0.32],
    params: { algMorph: 0.18, waveFold: 0.55, fmFeedback: 0.62, formantShift: 0.52, modAttack: 0.01, modDecay: 0.28, carAttack: 0.01, carDecay: 0.32, chorusDelayMix: 0.08, spaceReverbMix: 0.05 },
  },
  {
    id: 'bass-waveguide-sub-808',
    name: '2. Deep 808 Tube Sub',
    category: 'BASSES',
    engineBadge: 'WAVEGUIDE',
    featureBadge: 'SUB BOOM',
    tags: ['Bass', 'Sub', 'Warm', '808'],
    favorite: true,
    targetEngineId: 0,
    opRatios: [0.5, 0.5, 1.0, 1.5, 2.0, 1.0],
    opLevels: [1.0, 0.45, 0.82, 0.35, 0.20, 0.15],
    params: { algMorph: 0.10, waveFold: 0.32, fmFeedback: 0.35, formantShift: 0.38, modAttack: 0.01, modDecay: 0.42, carAttack: 0.01, carDecay: 0.60, chorusDelayMix: 0.04, spaceReverbMix: 0.04 },
  },
  {
    id: 'bass-moog-ladder-fat',
    name: '3. Fat Mini Ladder Bass',
    category: 'BASSES',
    engineBadge: '6-OP FM',
    featureBadge: 'ANALOG FILTER',
    tags: ['Bass', 'Fat', 'Funk', 'Synthwave'],
    favorite: true,
    targetEngineId: 0,
    opRatios: [0.5, 1.0, 1.004, 2.0, 1.5, 3.0],
    opLevels: [0.98, 0.88, 0.85, 0.64, 0.45, 0.38],
    params: { algMorph: 0.35, waveFold: 0.78, fmFeedback: 0.58, formantShift: 0.48, modAttack: 0.01, modDecay: 0.38, carAttack: 0.01, carDecay: 0.44, chorusDelayMix: 0.14, spaceReverbMix: 0.06 },
  },
  {
    id: 'bass-neuro-fold-reese',
    name: '4. West-Coast Reese Sub',
    category: 'BASSES',
    engineBadge: '6-OP FM',
    featureBadge: 'DETUNED REESE',
    tags: ['Bass', 'Reese', 'DnB', 'Growl'],
    favorite: false,
    targetEngineId: 0,
    opRatios: [0.5, 1.005, 0.995, 2.01, 3.0, 1.5],
    opLevels: [0.96, 0.88, 0.90, 0.78, 0.65, 0.72],
    params: { algMorph: 0.68, waveFold: 0.90, fmFeedback: 0.80, formantShift: 0.46, wowFlutter: 0.45, modAttack: 0.12, modDecay: 0.76, carAttack: 0.05, carDecay: 0.68, chorusDelayMix: 0.28, spaceReverbMix: 0.10 },
  },
  {
    id: 'bass-acid-303-squelch',
    name: '5. Acid 303 Squelch Line',
    category: 'BASSES',
    engineBadge: '6-OP FM',
    featureBadge: 'RESONANT ACID',
    tags: ['Bass', 'Acid', '303', 'Techno'],
    favorite: true,
    targetEngineId: 0,
    opRatios: [0.5, 1.0, 2.0, 3.0, 4.0, 6.0],
    opLevels: [0.98, 0.85, 0.78, 0.70, 0.55, 0.48],
    params: { algMorph: 0.25, waveFold: 0.85, fmFeedback: 0.88, formantShift: 0.62, vowelMorphX: 0.78, modAttack: 0.01, modDecay: 0.30, carAttack: 0.01, carDecay: 0.26, chorusDelayMix: 0.20, spaceReverbMix: 0.08 },
  },
  {
    id: 'bass-slap-funk-thumb',
    name: '6. Slap Funk Thumb Bass',
    category: 'BASSES',
    engineBadge: 'PHYSICAL MODEL',
    featureBadge: 'METALLIC SNAP',
    tags: ['Bass', 'Slap', 'Funk', 'String'],
    favorite: false,
    targetEngineId: 0,
    opRatios: [0.5, 1.0, 3.0, 7.0, 2.0, 9.0],
    opLevels: [0.98, 0.80, 0.72, 0.55, 0.64, 0.42],
    params: { algMorph: 0.20, waveFold: 0.48, fmFeedback: 0.64, formantShift: 0.70, modAttack: 0.01, modDecay: 0.22, carAttack: 0.01, carDecay: 0.35, chorusDelayMix: 0.15, spaceReverbMix: 0.10 },
  },
  {
    id: 'bass-synthwave-arp-drive',
    name: '7. Outrun Driving 16th Bass',
    category: 'BASSES',
    engineBadge: '6-OP FM',
    featureBadge: 'SYNTHWAVE',
    tags: ['Bass', '80s', 'Arp', 'Drive'],
    favorite: false,
    targetEngineId: 0,
    opRatios: [0.5, 1.0, 1.5, 2.0, 3.0, 2.0],
    opLevels: [0.99, 0.82, 0.76, 0.60, 0.42, 0.35],
    params: { algMorph: 0.30, waveFold: 0.68, fmFeedback: 0.52, formantShift: 0.56, limiterDrive: 0.50, modAttack: 0.01, modDecay: 0.25, carAttack: 0.01, carDecay: 0.28, chorusDelayMix: 0.22, spaceReverbMix: 0.08 },
  },
  {
    id: 'bass-upright-woody-jazz',
    name: '8. Acoustic Upright Double Bass',
    category: 'BASSES',
    engineBadge: 'WAVEGUIDE',
    featureBadge: 'WOODY BODY',
    tags: ['Bass', 'Jazz', 'Upright', 'Acoustic'],
    favorite: false,
    targetEngineId: 0,
    opRatios: [0.5, 1.0, 0.5, 1.0, 2.0, 1.0],
    opLevels: [1.00, 0.85, 0.45, 0.20, 0.10, 0.30],
    params: {
      algMorph: 0.02,
      waveFold: 0.00,
      fmFeedback: 0.06,
      formantShift: 0.28,
      modAttack: 0.006,
      modDecay: 0.22,
      carAttack: 0.012,
      carDecay: 0.55,
      chorusDelayMix: 0.00,
      spaceReverbMix: 0.16,
      cutoff: 0.32,
      resonance: 0.22,
      drive: 0.08,
      waveShape: 0.02,
      ampAttack: 0.018,
      ampDecay: 0.50,
      ampSustain: 0.12,
      ampRelease: 0.30,
      fltEnvAmount: 0.65,
      fltDecay: 0.25,
      reverbSpace: 0.16,
    },
  },
  ...createCategoryUserSlots('BASSES', {
    engineBadge: 'USER CUSTOM',
    featureBadge: 'USER SLOT',
    tags: ['User', 'Bass', 'Custom'],
    favorite: false,
    targetEngineId: 0,
    opRatios: [0.5, 1.0, 0.5, 2.0, 3.0, 4.0],
    opLevels: [0.98, 0.82, 0.88, 0.60, 0.40, 0.30],
    params: { algMorph: 0.22, waveFold: 0.58, fmFeedback: 0.55, formantShift: 0.50, modAttack: 0.01, modDecay: 0.32, carAttack: 0.01, carDecay: 0.38, chorusDelayMix: 0.10, spaceReverbMix: 0.06 },
  }),

  // ===========================================================================
  // 3. LEADS & BRASS — 8 Most Popular Sounds + 4 User Slots
  // ===========================================================================
  {
    id: 'lead-cs80-ribbon-brass',
    name: '1. Blade Runner CS-80 Brass',
    category: 'LEADS_BRASS',
    engineBadge: '6-OP FM',
    featureBadge: 'MPE EXPRESSIVE',
    tags: ['Lead', 'Brass', 'Cinematic', 'Warm'],
    favorite: true,
    targetEngineId: 0,
    opRatios: [1.0, 1.0, 2.0, 1.004, 3.0, 4.0],
    opLevels: [0.95, 0.82, 0.88, 0.76, 0.58, 0.45],
    params: { algMorph: 0.45, waveFold: 0.68, fmFeedback: 0.58, formantShift: 0.64, modAttack: 0.28, modDecay: 0.78, carAttack: 0.16, carDecay: 0.68, chorusDelayMix: 0.42, spaceReverbMix: 0.46 },
  },
  {
    id: 'lead-prophet-sync-solo',
    name: '2. Prophet-5 Poly Brass',
    category: 'LEADS_BRASS',
    engineBadge: '6-OP FM',
    featureBadge: 'ANALOG BRASS',
    tags: ['Lead', 'Brass', 'Analog', 'Poly'],
    favorite: true,
    targetEngineId: 0,
    opRatios: [1.0, 2.0, 1.0, 3.0, 1.5, 2.5],
    opLevels: [0.96, 0.78, 0.84, 0.65, 0.50, 0.55],
    params: { algMorph: 0.52, waveFold: 0.76, fmFeedback: 0.52, formantShift: 0.66, modAttack: 0.08, modDecay: 0.64, carAttack: 0.04, carDecay: 0.54, chorusDelayMix: 0.36, spaceReverbMix: 0.30 },
  },
  {
    id: 'lead-juno-106-hoover',
    name: '3. Juno-106 Chorus Lead',
    category: 'LEADS_BRASS',
    engineBadge: '6-OP FM',
    featureBadge: 'BBD CHORUS',
    tags: ['Lead', 'Juno', 'Chorus', '80s'],
    favorite: true,
    targetEngineId: 0,
    opRatios: [1.0, 0.5, 2.0, 1.0, 3.0, 4.0],
    opLevels: [0.95, 0.75, 0.82, 0.68, 0.48, 0.36],
    params: { algMorph: 0.48, waveFold: 0.82, fmFeedback: 0.45, formantShift: 0.70, modAttack: 0.02, modDecay: 0.60, carAttack: 0.02, carDecay: 0.52, chorusDelayMix: 0.55, spaceReverbMix: 0.28 },
  },
  {
    id: 'lead-mini-funk-saw',
    name: '4. P-Funk Sawtooth Solo',
    category: 'LEADS_BRASS',
    engineBadge: '6-OP FM',
    featureBadge: 'FILTER GLIDE',
    tags: ['Lead', 'Funk', 'Solo', 'Moog'],
    favorite: false,
    targetEngineId: 0,
    opRatios: [1.0, 1.0, 2.0, 3.0, 4.0, 2.0],
    opLevels: [0.98, 0.85, 0.78, 0.62, 0.48, 0.40],
    params: { algMorph: 0.32, waveFold: 0.88, fmFeedback: 0.68, formantShift: 0.72, vowelMorphX: 0.65, modAttack: 0.04, modDecay: 0.58, carAttack: 0.01, carDecay: 0.46, chorusDelayMix: 0.28, spaceReverbMix: 0.22 },
  },
  {
    id: 'lead-toto-africa-brass',
    name: '5. 1982 Toto Horn Section',
    category: 'LEADS_BRASS',
    engineBadge: '6-OP FM',
    featureBadge: 'ENSEMBLE HORNS',
    tags: ['Brass', '80s', 'Horns', 'Pop'],
    favorite: true,
    targetEngineId: 0,
    opRatios: [1.0, 1.0, 2.0, 3.0, 1.0, 2.0],
    opLevels: [0.96, 0.80, 0.88, 0.70, 0.62, 0.52],
    params: { algMorph: 0.42, waveFold: 0.64, fmFeedback: 0.54, formantShift: 0.62, modAttack: 0.14, modDecay: 0.68, carAttack: 0.08, carDecay: 0.58, chorusDelayMix: 0.40, spaceReverbMix: 0.34 },
  },
  {
    id: 'lead-buchla-westcoast-LPG',
    name: '6. West-Coast Wavefold Pluck',
    category: 'LEADS_BRASS',
    engineBadge: 'WAVEGUIDE',
    featureBadge: 'WAVEFOLDER',
    tags: ['Lead', 'Buchla', 'Wavefold', 'Modular'],
    favorite: false,
    targetEngineId: 0,
    opRatios: [1.0, 2.0, 1.5, 3.0, 5.0, 7.0],
    opLevels: [0.95, 0.82, 0.76, 0.68, 0.54, 0.44],
    params: { algMorph: 0.58, waveFold: 0.94, fmFeedback: 0.72, formantShift: 0.68, modAttack: 0.01, modDecay: 0.36, carAttack: 0.01, carDecay: 0.40, chorusDelayMix: 0.34, spaceReverbMix: 0.26 },
  },
  {
    id: 'lead-chiptune-square-8bit',
    name: '7. Arcade Pulse Wave Lead',
    category: 'LEADS_BRASS',
    engineBadge: '6-OP FM',
    featureBadge: 'PULSE WIDTH',
    tags: ['Lead', 'Pulse', 'Retro', 'Arcade'],
    favorite: false,
    targetEngineId: 0,
    opRatios: [1.0, 2.0, 3.0, 1.0, 5.0, 7.0],
    opLevels: [0.98, 0.45, 0.82, 0.35, 0.64, 0.40],
    params: { algMorph: 0.65, waveFold: 0.75, fmFeedback: 0.40, formantShift: 0.76, modAttack: 0.01, modDecay: 0.48, carAttack: 0.01, carDecay: 0.42, chorusDelayMix: 0.24, spaceReverbMix: 0.18 },
  },
  {
    id: 'lead-bamboo-shakuhachi-flute',
    name: '8. Expressive Breath Flute',
    category: 'LEADS_BRASS',
    engineBadge: 'WAVEGUIDE',
    featureBadge: 'BREATH TUBE',
    tags: ['Lead', 'Flute', 'Wind', 'Organic'],
    favorite: false,
    targetEngineId: 0,
    opRatios: [1.0, 2.0, 3.0, 1.0, 4.0, 2.0],
    opLevels: [0.94, 0.52, 0.40, 0.35, 0.22, 0.18],
    params: { algMorph: 0.38, waveFold: 0.28, fmFeedback: 0.32, formantShift: 0.60, sibilance: 0.68, wowFlutter: 0.38, modAttack: 0.22, modDecay: 0.74, carAttack: 0.18, carDecay: 0.66, chorusDelayMix: 0.32, spaceReverbMix: 0.45 },
  },
  ...createCategoryUserSlots('LEADS_BRASS', {
    engineBadge: 'USER CUSTOM',
    featureBadge: 'USER SLOT',
    tags: ['User', 'Lead', 'Custom'],
    favorite: false,
    targetEngineId: 0,
    opRatios: [1.0, 1.0, 2.0, 3.0, 1.5, 4.0],
    opLevels: [0.95, 0.78, 0.85, 0.65, 0.50, 0.40],
    params: { algMorph: 0.45, waveFold: 0.70, fmFeedback: 0.55, formantShift: 0.65, modAttack: 0.08, modDecay: 0.65, carAttack: 0.05, carDecay: 0.58, chorusDelayMix: 0.38, spaceReverbMix: 0.32 },
  }),

  // ===========================================================================
  // 4. PADS & STRINGS — 8 Most Popular Sounds + 4 User Slots
  // ===========================================================================
  {
    id: 'pad-oberheim-velvet-strings',
    name: '1. OB-Xa Golden Strings',
    category: 'PADS_STRINGS',
    engineBadge: '6-OP FM',
    featureBadge: 'BBD CHORUS',
    tags: ['Pad', 'Strings', 'Warm', 'Lush'],
    favorite: true,
    targetEngineId: 0,
    opRatios: [1.0, 2.0, 1.003, 0.997, 3.0, 4.0],
    opLevels: [0.90, 0.65, 0.88, 0.62, 0.45, 0.38],
    params: { algMorph: 0.58, waveFold: 0.48, fmFeedback: 0.44, formantShift: 0.58, modAttack: 0.48, modDecay: 0.85, carAttack: 0.42, carDecay: 0.88, chorusDelayMix: 0.48, spaceReverbMix: 0.55 },
  },
  {
    id: 'pad-evolving-aurora-morph',
    name: '2. Evolving Aurora Cathedral',
    category: 'PADS_STRINGS',
    engineBadge: 'VOCODER / FM',
    featureBadge: 'SHIMMER HALL',
    tags: ['Pad', 'Ambient', 'Cinematic'],
    favorite: true,
    targetEngineId: 0,
    opRatios: [0.5, 1.5, 1.0, 3.0, 2.0, 6.0],
    opLevels: [0.88, 0.60, 0.92, 0.55, 0.48, 0.42],
    params: { algMorph: 0.74, waveFold: 0.34, fmFeedback: 0.38, formantShift: 0.64, modAttack: 0.58, modDecay: 0.90, carAttack: 0.52, carDecay: 0.92, chorusDelayMix: 0.52, spaceReverbMix: 0.68 },
  },
  {
    id: 'pad-solina-string-ensemble',
    name: '3. 1976 Solina String Machine',
    category: 'PADS_STRINGS',
    engineBadge: '6-OP FM',
    featureBadge: 'ENSEMBLE',
    tags: ['Strings', 'Vintage', 'Disco', 'Pad'],
    favorite: true,
    targetEngineId: 0,
    opRatios: [1.0, 2.0, 1.0, 4.0, 3.0, 2.0],
    opLevels: [0.92, 0.74, 0.86, 0.58, 0.42, 0.36],
    params: { algMorph: 0.62, waveFold: 0.58, fmFeedback: 0.42, formantShift: 0.66, wowFlutter: 0.36, modAttack: 0.35, modDecay: 0.82, carAttack: 0.30, carDecay: 0.84, chorusDelayMix: 0.58, spaceReverbMix: 0.44 },
  },
  {
    id: 'pad-jupiter8-warm-sweep',
    name: '4. Jupiter-8 Resonant Sweep',
    category: 'PADS_STRINGS',
    engineBadge: '6-OP FM',
    featureBadge: 'FILTER SWEEP',
    tags: ['Pad', 'Sweep', 'Jupiter', '80s'],
    favorite: false,
    targetEngineId: 0,
    opRatios: [0.5, 1.0, 2.0, 1.5, 3.0, 4.0],
    opLevels: [0.94, 0.82, 0.88, 0.64, 0.52, 0.40],
    params: { algMorph: 0.50, waveFold: 0.66, fmFeedback: 0.60, formantShift: 0.54, vowelMorphX: 0.55, modAttack: 0.52, modDecay: 0.86, carAttack: 0.36, carDecay: 0.86, chorusDelayMix: 0.46, spaceReverbMix: 0.52 },
  },
  {
    id: 'pad-mellotron-tape-strings',
    name: '5. Mellotron M400 Tape Violins',
    category: 'PADS_STRINGS',
    engineBadge: 'WAVEGUIDE',
    featureBadge: 'TAPE FLUTTER',
    tags: ['Strings', 'Mellotron', 'Tape', 'Retro'],
    favorite: true,
    targetEngineId: 0,
    opRatios: [1.0, 2.0, 3.0, 1.5, 4.0, 2.5],
    opLevels: [0.95, 0.70, 0.58, 0.42, 0.35, 0.28],
    params: { algMorph: 0.44, waveFold: 0.42, fmFeedback: 0.36, formantShift: 0.48, wowFlutter: 0.65, modAttack: 0.28, modDecay: 0.78, carAttack: 0.25, carDecay: 0.76, chorusDelayMix: 0.38, spaceReverbMix: 0.42 },
  },
  {
    id: 'pad-dark-bladerunner-drone',
    name: '6. Subterranean Cinema Drone',
    category: 'PADS_STRINGS',
    engineBadge: 'WAVEGUIDE',
    featureBadge: 'DEEP DRONE',
    tags: ['Pad', 'Dark', 'Sci-Fi', 'Drone'],
    favorite: false,
    targetEngineId: 0,
    opRatios: [0.5, 0.5, 1.0, 1.5, 2.0, 3.0],
    opLevels: [0.98, 0.84, 0.88, 0.62, 0.48, 0.40],
    params: { algMorph: 0.36, waveFold: 0.52, fmFeedback: 0.50, formantShift: 0.36, wowFlutter: 0.42, modAttack: 0.64, modDecay: 0.92, carAttack: 0.58, carDecay: 0.94, chorusDelayMix: 0.48, spaceReverbMix: 0.72 },
  },
  {
    id: 'pad-crystal-fifth-cloud',
    name: '7.Harmonic 5th Dream Cloud',
    category: 'PADS_STRINGS',
    engineBadge: '6-OP FM',
    featureBadge: 'FIFTH HARMONIC',
    tags: ['Pad', 'Fifth', 'Ambient', 'Dreamy'],
    favorite: false,
    targetEngineId: 0,
    opRatios: [1.0, 1.5, 2.0, 3.0, 1.5, 4.5],
    opLevels: [0.92, 0.78, 0.85, 0.65, 0.55, 0.40],
    params: { algMorph: 0.68, waveFold: 0.30, fmFeedback: 0.32, formantShift: 0.62, modAttack: 0.45, modDecay: 0.88, carAttack: 0.40, carDecay: 0.90, chorusDelayMix: 0.54, spaceReverbMix: 0.64 },
  },
  {
    id: 'pad-analog-warm-organ-swell',
    name: '8. Analog Harmonium Swell',
    category: 'PADS_STRINGS',
    engineBadge: '6-OP FM',
    featureBadge: 'WARM SWELL',
    tags: ['Pad', 'Warm', 'Chords', 'Analog'],
    favorite: false,
    targetEngineId: 0,
    opRatios: [1.0, 0.5, 1.0, 2.0, 3.0, 1.0],
    opLevels: [0.94, 0.68, 0.86, 0.58, 0.38, 0.30],
    params: { algMorph: 0.55, waveFold: 0.38, fmFeedback: 0.30, formantShift: 0.52, modAttack: 0.38, modDecay: 0.84, carAttack: 0.34, carDecay: 0.86, chorusDelayMix: 0.42, spaceReverbMix: 0.50 },
  },
  ...createCategoryUserSlots('PADS_STRINGS', {
    engineBadge: 'USER CUSTOM',
    featureBadge: 'USER SLOT',
    tags: ['User', 'Pad', 'Custom'],
    favorite: false,
    targetEngineId: 0,
    opRatios: [1.0, 2.0, 1.003, 0.997, 1.5, 3.0],
    opLevels: [0.90, 0.65, 0.88, 0.62, 0.45, 0.38],
    params: { algMorph: 0.58, waveFold: 0.45, fmFeedback: 0.40, formantShift: 0.58, modAttack: 0.45, modDecay: 0.85, carAttack: 0.40, carDecay: 0.88, chorusDelayMix: 0.48, spaceReverbMix: 0.55 },
  }),

  // ===========================================================================
  // 5. BELLS & MALLETS — 8 Most Popular Sounds + 4 User Slots
  // ===========================================================================
  {
    id: 'bells-tubular-cathedral',
    name: '1. Tubular Brass Chimes',
    category: 'BELLS_MALLETS',
    engineBadge: 'PHYSICAL MODEL',
    featureBadge: 'INHARMONIC',
    tags: ['Bells', 'Metallic', 'Chime'],
    favorite: true,
    targetEngineId: 0,
    opRatios: [1.0, 2.756, 1.0, 5.404, 3.5, 8.93],
    opLevels: [0.95, 0.78, 0.85, 0.65, 0.50, 0.42],
    params: { algMorph: 0.28, waveFold: 0.15, fmFeedback: 0.32, formantShift: 0.72, modAttack: 0.01, modDecay: 0.68, carAttack: 0.01, carDecay: 0.82, chorusDelayMix: 0.32, spaceReverbMix: 0.48 },
  },
  {
    id: 'bells-rosewood-marimba',
    name: '2. Rosewood Concert Marimba',
    category: 'BELLS_MALLETS',
    engineBadge: 'WAVEGUIDE',
    featureBadge: 'TUBE CAVITY',
    tags: ['Mallet', 'Woody', 'Organic'],
    favorite: true,
    targetEngineId: 0,
    opRatios: [1.0, 4.0, 1.0, 9.2, 2.0, 6.0],
    opLevels: [0.98, 0.68, 0.82, 0.42, 0.30, 0.20],
    params: { algMorph: 0.12, waveFold: 0.18, fmFeedback: 0.18, formantShift: 0.58, modAttack: 0.01, modDecay: 0.18, carAttack: 0.01, carDecay: 0.32, chorusDelayMix: 0.20, spaceReverbMix: 0.28 },
  },
  {
    id: 'bells-jazz-vibraphone-motor',
    name: '3. Modern Jazz Vibraphone',
    category: 'BELLS_MALLETS',
    engineBadge: '6-OP FM',
    featureBadge: 'TREMOLO VIBE',
    tags: ['Mallet', 'Vibes', 'Jazz', 'Warm'],
    favorite: true,
    targetEngineId: 0,
    opRatios: [1.0, 4.0, 1.0, 3.0, 7.0, 1.0],
    opLevels: [0.95, 0.52, 0.86, 0.38, 0.22, 0.25],
    params: { algMorph: 0.24, waveFold: 0.10, fmFeedback: 0.20, formantShift: 0.64, wowFlutter: 0.40, modAttack: 0.01, modDecay: 0.58, carAttack: 0.01, carDecay: 0.74, chorusDelayMix: 0.42, spaceReverbMix: 0.40 },
  },
  {
    id: 'bells-celesta-music-box',
    name: '4. Enchanted Celesta Box',
    category: 'BELLS_MALLETS',
    engineBadge: '6-OP FM',
    featureBadge: 'CRYSTAL TINE',
    tags: ['Bells', 'Celesta', 'Music Box'],
    favorite: false,
    targetEngineId: 0,
    opRatios: [2.0, 6.0, 1.0, 8.0, 4.0, 10.0],
    opLevels: [0.94, 0.58, 0.82, 0.44, 0.35, 0.28],
    params: { algMorph: 0.34, waveFold: 0.08, fmFeedback: 0.22, formantShift: 0.78, modAttack: 0.01, modDecay: 0.48, carAttack: 0.01, carDecay: 0.66, chorusDelayMix: 0.38, spaceReverbMix: 0.52 },
  },
  {
    id: 'bells-kalimba-thumb-piano',
    name: '5. African Kalimba Thumb',
    category: 'BELLS_MALLETS',
    engineBadge: 'PHYSICAL MODEL',
    featureBadge: 'THUMB TINE',
    tags: ['Mallet', 'Kalimba', 'Organic', 'Pluck'],
    favorite: true,
    targetEngineId: 0,
    opRatios: [1.0, 6.2, 1.0, 2.0, 9.5, 3.0],
    opLevels: [0.96, 0.54, 0.84, 0.40, 0.28, 0.20],
    params: { algMorph: 0.18, waveFold: 0.14, fmFeedback: 0.24, formantShift: 0.66, modAttack: 0.01, modDecay: 0.26, carAttack: 0.01, carDecay: 0.42, chorusDelayMix: 0.28, spaceReverbMix: 0.36 },
  },
  {
    id: 'bells-steel-pan-island',
    name: '6. Trinidad Steel Drum Pan',
    category: 'BELLS_MALLETS',
    engineBadge: 'PHYSICAL MODEL',
    featureBadge: 'HARMONIC SHELL',
    tags: ['Mallet', 'Steel Drum', 'Tropical'],
    favorite: false,
    targetEngineId: 0,
    opRatios: [1.0, 2.0, 2.98, 4.02, 5.0, 6.0],
    opLevels: [0.95, 0.78, 0.70, 0.55, 0.40, 0.30],
    params: { algMorph: 0.40, waveFold: 0.35, fmFeedback: 0.45, formantShift: 0.70, modAttack: 0.01, modDecay: 0.38, carAttack: 0.01, carDecay: 0.46, chorusDelayMix: 0.34, spaceReverbMix: 0.32 },
  },
  {
    id: 'bells-balinese-gamelan-gong',
    name: '7. Balinese Gamelan Metallophone',
    category: 'BELLS_MALLETS',
    engineBadge: 'WAVEGUIDE',
    featureBadge: 'BRONZE RESONATOR',
    tags: ['Bells', 'Gamelan', 'Bronze', 'Cinematic'],
    favorite: false,
    targetEngineId: 0,
    opRatios: [1.0, 2.41, 1.01, 3.82, 5.14, 6.7],
    opLevels: [0.95, 0.80, 0.88, 0.65, 0.48, 0.38],
    params: { algMorph: 0.32, waveFold: 0.28, fmFeedback: 0.40, formantShift: 0.62, modAttack: 0.01, modDecay: 0.60, carAttack: 0.01, carDecay: 0.78, chorusDelayMix: 0.30, spaceReverbMix: 0.50 },
  },
  {
    id: 'bells-Tibetan-singing-bowl',
    name: '8. Tibetan Singing Bowl',
    category: 'BELLS_MALLETS',
    engineBadge: 'WAVEGUIDE',
    featureBadge: 'LONG RING',
    tags: ['Bells', 'Bowl', 'Ambient', 'Meditation'],
    favorite: false,
    targetEngineId: 0,
    opRatios: [1.0, 2.71, 1.002, 5.15, 1.5, 4.0],
    opLevels: [0.94, 0.68, 0.90, 0.48, 0.40, 0.32],
    params: { algMorph: 0.50, waveFold: 0.12, fmFeedback: 0.25, formantShift: 0.56, modAttack: 0.12, modDecay: 0.90, carAttack: 0.08, carDecay: 0.95, chorusDelayMix: 0.40, spaceReverbMix: 0.68 },
  },
  ...createCategoryUserSlots('BELLS_MALLETS', {
    engineBadge: 'USER CUSTOM',
    featureBadge: 'USER SLOT',
    tags: ['User', 'Bell', 'Mallet', 'Custom'],
    favorite: false,
    targetEngineId: 0,
    opRatios: [1.0, 3.5, 1.0, 5.4, 2.0, 7.0],
    opLevels: [0.95, 0.65, 0.85, 0.50, 0.40, 0.30],
    params: { algMorph: 0.25, waveFold: 0.16, fmFeedback: 0.28, formantShift: 0.68, modAttack: 0.01, modDecay: 0.55, carAttack: 0.01, carDecay: 0.70, chorusDelayMix: 0.32, spaceReverbMix: 0.42 },
  }),

  // ===========================================================================
  // 6. PHYSICAL DRUMS — 8 Most Popular Sounds + 4 User Slots
  // ===========================================================================
  {
    id: 'drums-analog-waveguide-808',
    name: '1. Studio TR-808 Sub Kit',
    category: 'PHYSICAL_DRUMS',
    engineBadge: 'WAVEGUIDE',
    featureBadge: 'BOOM 808 KIT',
    tags: ['Drums', '808', 'Sub Kick', 'Crispy Hats'],
    favorite: true,
    targetEngineId: 1,
    opRatios: [1.0, 1.5, 2.0, 2.5, 3.0, 4.0],
    opLevels: [0.95, 0.80, 0.75, 0.65, 0.55, 0.50],
    params: { resonatorDecay: 0.78, waveFold: 0.36, drumDispersion: 0.35, limiterDrive: 0.35, chorusDelayMix: 0.10, spaceReverbMix: 0.14 },
  },
  {
    id: 'drums-punchy-909-house-kit',
    name: '2. Classic TR-909 House Kit',
    category: 'PHYSICAL_DRUMS',
    engineBadge: 'PHYSICAL MODEL',
    featureBadge: 'PUNCHY 909',
    tags: ['Drums', '909', 'House', 'Techno'],
    favorite: true,
    targetEngineId: 1,
    opRatios: [1.0, 1.5, 2.0, 2.5, 3.0, 4.0],
    opLevels: [0.98, 0.85, 0.80, 0.72, 0.60, 0.55],
    params: { resonatorDecay: 0.55, waveFold: 0.58, drumDispersion: 0.52, limiterDrive: 0.48, chorusDelayMix: 0.14, spaceReverbMix: 0.20 },
  },
  {
    id: 'drums-linndrum-80s-pop-kit',
    name: '3. 1984 Linn Pop Studio Kit',
    category: 'PHYSICAL_DRUMS',
    engineBadge: 'WAVEGUIDE',
    featureBadge: 'GATED REVERB',
    tags: ['Drums', '80s', 'Pop', 'Synthwave'],
    favorite: true,
    targetEngineId: 1,
    opRatios: [1.0, 1.5, 2.0, 2.5, 3.0, 4.0],
    opLevels: [0.96, 0.82, 0.78, 0.68, 0.58, 0.52],
    params: { resonatorDecay: 0.64, waveFold: 0.44, drumDispersion: 0.44, limiterDrive: 0.40, chorusDelayMix: 0.18, spaceReverbMix: 0.38 },
  },
  {
    id: 'drums-industrial-fold-kit',
    name: '4. Industrial Overdrive Kit',
    category: 'PHYSICAL_DRUMS',
    engineBadge: 'PHYSICAL MODEL',
    featureBadge: 'WAVEFOLD DRIVE',
    tags: ['Drums', 'Gritty', 'Techno', 'Industrial'],
    favorite: false,
    targetEngineId: 1,
    opRatios: [1.0, 1.41, 2.23, 3.16, 4.12, 5.65],
    opLevels: [1.0, 0.88, 0.85, 0.78, 0.70, 0.65],
    params: { resonatorDecay: 0.62, waveFold: 0.88, drumDispersion: 0.68, limiterDrive: 0.72, chorusDelayMix: 0.22, spaceReverbMix: 0.28 },
  },
  {
    id: 'drums-lofi-boom-bap-kit',
    name: '5. Dusty Vinyl Boom-Bap Kit',
    category: 'PHYSICAL_DRUMS',
    engineBadge: 'WAVEGUIDE',
    featureBadge: 'TAPE CRUNCH',
    tags: ['Drums', 'Hip-Hop', 'Lo-Fi', 'Boom Bap'],
    favorite: true,
    targetEngineId: 1,
    opRatios: [1.0, 1.5, 2.0, 2.5, 3.0, 4.0],
    opLevels: [0.96, 0.84, 0.70, 0.62, 0.55, 0.48],
    params: { resonatorDecay: 0.42, waveFold: 0.52, drumDispersion: 0.28, wowFlutter: 0.42, limiterDrive: 0.55, chorusDelayMix: 0.08, spaceReverbMix: 0.12 },
  },
  {
    id: 'drums-afro-tribal-perc-kit',
    name: '6. Wooden Log & Conga Kit',
    category: 'PHYSICAL_DRUMS',
    engineBadge: 'WAVEGUIDE',
    featureBadge: 'HOLLOW TUBE',
    tags: ['Drums', 'Percussion', 'Organic', 'Wood'],
    favorite: false,
    targetEngineId: 1,
    opRatios: [1.0, 1.5, 2.0, 2.5, 3.0, 4.0],
    opLevels: [0.94, 0.80, 0.72, 0.64, 0.58, 0.50],
    params: { resonatorDecay: 0.50, waveFold: 0.22, drumDispersion: 0.62, limiterDrive: 0.28, chorusDelayMix: 0.16, spaceReverbMix: 0.26 },
  },
  {
    id: 'drums-electro-kraft-kit',
    name: '7. Autobahn Electro Zap Kit',
    category: 'PHYSICAL_DRUMS',
    engineBadge: '6-OP FM',
    featureBadge: 'SYNTH PERC',
    tags: ['Drums', 'Electro', 'Kraft', 'Retro'],
    favorite: false,
    targetEngineId: 1,
    opRatios: [1.0, 1.5, 2.0, 2.5, 3.0, 4.0],
    opLevels: [0.95, 0.82, 0.76, 0.68, 0.60, 0.54],
    params: { resonatorDecay: 0.48, waveFold: 0.64, drumDispersion: 0.75, limiterDrive: 0.42, chorusDelayMix: 0.25, spaceReverbMix: 0.22 },
  },
  {
    id: 'drums-dub-echo-chamber-kit',
    name: '8. Kingston Dub Delay Kit',
    category: 'PHYSICAL_DRUMS',
    engineBadge: 'WAVEGUIDE',
    featureBadge: 'TAPE ECHO',
    tags: ['Drums', 'Dub', 'Echo', 'Space'],
    favorite: false,
    targetEngineId: 1,
    opRatios: [1.0, 1.5, 2.0, 2.5, 3.0, 4.0],
    opLevels: [0.95, 0.82, 0.75, 0.65, 0.58, 0.50],
    params: { resonatorDecay: 0.68, waveFold: 0.40, drumDispersion: 0.48, limiterDrive: 0.36, chorusDelayMix: 0.62, spaceReverbMix: 0.58 },
  },
  ...createCategoryUserSlots('PHYSICAL_DRUMS', {
    engineBadge: 'USER CUSTOM',
    featureBadge: 'USER SLOT',
    tags: ['User', 'Drums', 'Custom'],
    favorite: false,
    targetEngineId: 1,
    opRatios: [1.0, 1.5, 2.0, 2.5, 3.0, 4.0],
    opLevels: [0.95, 0.80, 0.75, 0.65, 0.55, 0.50],
    params: { resonatorDecay: 0.65, waveFold: 0.45, drumDispersion: 0.42, limiterDrive: 0.35, chorusDelayMix: 0.14, spaceReverbMix: 0.18 },
  }),

  // ===========================================================================
  // 7. VOCODER & CHOIR — 8 Most Popular Sounds + 4 User Slots
  // ===========================================================================
  {
    id: 'voc-daft-robot-choir',
    name: '1. Daft Punk Talkbox Choir',
    category: 'VOCODER_CHOIR',
    engineBadge: 'VOCODER / FM',
    featureBadge: 'FORMANT MATRIX',
    tags: ['Vocoder', 'Robot', 'Choir', 'Formant'],
    favorite: true,
    targetEngineId: 2,
    opRatios: [1.0, 2.0, 3.0, 4.0, 5.0, 6.0],
    opLevels: [0.98, 0.88, 0.82, 0.76, 0.68, 0.60],
    params: { formantShift: 0.58, vowelMorphX: 0.42, vowelMorphY: 0.72, waveFold: 0.74, modAttack: 0.05, modDecay: 0.72, carAttack: 0.04, carDecay: 0.64, chorusDelayMix: 0.38, spaceReverbMix: 0.34 },
  },
  {
    id: 'voc-vowel-cathedral-pad',
    name: '2. Angelic Seraphim Choir',
    category: 'VOCODER_CHOIR',
    engineBadge: 'VOCODER / FM',
    featureBadge: 'AAH / OOH CHOIR',
    tags: ['Vocoder', 'Vocal', 'Ambient', 'Choir'],
    favorite: true,
    targetEngineId: 2,
    opRatios: [1.0, 1.002, 2.0, 3.0, 4.0, 0.5],
    opLevels: [0.95, 0.90, 0.84, 0.70, 0.55, 0.65],
    params: { formantShift: 0.64, vowelMorphX: 0.52, vowelMorphY: 0.78, waveFold: 0.58, sibilance: 0.35, modAttack: 0.38, modDecay: 0.84, carAttack: 0.35, carDecay: 0.85, chorusDelayMix: 0.48, spaceReverbMix: 0.58 },
  },
  {
    id: 'voc-kraftwerk-computer-voice',
    name: '3. Vocoder 1981 Robot Lead',
    category: 'VOCODER_CHOIR',
    engineBadge: 'VOCODER / FM',
    featureBadge: 'VOWEL STEP',
    tags: ['Vocoder', 'Electro', 'Robot', 'Lead'],
    favorite: true,
    targetEngineId: 2,
    opRatios: [1.0, 2.0, 1.5, 3.0, 4.0, 6.0],
    opLevels: [0.98, 0.86, 0.80, 0.74, 0.65, 0.58],
    params: { formantShift: 0.52, vowelMorphX: 0.22, vowelMorphY: 0.82, waveFold: 0.82, sibilance: 0.28, modAttack: 0.02, modDecay: 0.58, carAttack: 0.02, carDecay: 0.52, chorusDelayMix: 0.32, spaceReverbMix: 0.25 },
  },
  {
    id: 'voc-california-talkbox-lead',
    name: '4. West-Coast Funk Talkbox',
    category: 'VOCODER_CHOIR',
    engineBadge: 'VOCODER / FM',
    featureBadge: 'TUBE TALKBOX',
    tags: ['Vocoder', 'Talkbox', 'Funk', 'Lead'],
    favorite: false,
    targetEngineId: 2,
    opRatios: [1.0, 2.0, 3.0, 4.0, 2.0, 1.0],
    opLevels: [0.99, 0.90, 0.84, 0.78, 0.70, 0.62],
    params: { formantShift: 0.62, vowelMorphX: 0.68, vowelMorphY: 0.85, waveFold: 0.88, modAttack: 0.02, modDecay: 0.65, carAttack: 0.01, carDecay: 0.48, chorusDelayMix: 0.30, spaceReverbMix: 0.22 },
  },
  {
    id: 'voc-gregorian-monk-chants',
    name: '5. Deep Gregorian Bass Chant',
    category: 'VOCODER_CHOIR',
    engineBadge: 'VOCODER / FM',
    featureBadge: 'LOW FORMANT',
    tags: ['Choir', 'Monks', 'Chant', 'Cinematic'],
    favorite: false,
    targetEngineId: 2,
    opRatios: [0.5, 1.0, 1.5, 2.0, 3.0, 4.0],
    opLevels: [0.98, 0.90, 0.82, 0.72, 0.58, 0.48],
    params: { formantShift: 0.38, vowelMorphX: 0.76, vowelMorphY: 0.74, waveFold: 0.54, modAttack: 0.42, modDecay: 0.88, carAttack: 0.38, carDecay: 0.88, chorusDelayMix: 0.44, spaceReverbMix: 0.68 },
  },
  {
    id: 'voc-fairlight-arr1-breath',
    name: '6. 1983 Fairlight Breath Vox',
    category: 'VOCODER_CHOIR',
    engineBadge: 'VOCODER / FM',
    featureBadge: 'AIRY VOX',
    tags: ['Vocoder', '80s', 'Breath', 'Vox'],
    favorite: true,
    targetEngineId: 2,
    opRatios: [1.0, 2.0, 4.0, 3.0, 5.0, 2.0],
    opLevels: [0.95, 0.84, 0.76, 0.68, 0.58, 0.50],
    params: { formantShift: 0.68, vowelMorphX: 0.34, vowelMorphY: 0.68, sibilance: 0.62, waveFold: 0.60, modAttack: 0.18, modDecay: 0.76, carAttack: 0.14, carDecay: 0.74, chorusDelayMix: 0.46, spaceReverbMix: 0.52 },
  },
  {
    id: 'voc-cyberpunk-throat-bass',
    name: '7. Cyber Throat Growl Bass',
    category: 'VOCODER_CHOIR',
    engineBadge: 'VOCODER / FM',
    featureBadge: 'TALKING BASS',
    tags: ['Vocoder', 'Bass', 'Throat', 'Cyber'],
    favorite: false,
    targetEngineId: 2,
    opRatios: [0.5, 1.0, 2.0, 3.0, 4.0, 1.5],
    opLevels: [0.99, 0.92, 0.86, 0.78, 0.70, 0.64],
    params: { formantShift: 0.46, vowelMorphX: 0.58, vowelMorphY: 0.88, waveFold: 0.92, fmFeedback: 0.74, modAttack: 0.04, modDecay: 0.55, carAttack: 0.02, carDecay: 0.52, chorusDelayMix: 0.26, spaceReverbMix: 0.20 },
  },
  {
    id: 'voc-shimmer-soprano-pad',
    name: '8. Valhalla Soprano Shimmer',
    category: 'VOCODER_CHOIR',
    engineBadge: 'VOCODER / FM',
    featureBadge: 'HIGH VOWEL',
    tags: ['Choir', 'Soprano', 'Shimmer', 'Pad'],
    favorite: false,
    targetEngineId: 2,
    opRatios: [1.0, 2.0, 2.003, 4.0, 3.0, 6.0],
    opLevels: [0.94, 0.88, 0.84, 0.68, 0.56, 0.45],
    params: { formantShift: 0.74, vowelMorphX: 0.48, vowelMorphY: 0.76, sibilance: 0.42, waveFold: 0.52, modAttack: 0.48, modDecay: 0.90, carAttack: 0.45, carDecay: 0.92, chorusDelayMix: 0.54, spaceReverbMix: 0.66 },
  },
  ...createCategoryUserSlots('VOCODER_CHOIR', {
    engineBadge: 'USER CUSTOM',
    featureBadge: 'USER SLOT',
    tags: ['User', 'Vocoder', 'Choir', 'Custom'],
    favorite: false,
    targetEngineId: 2,
    opRatios: [1.0, 2.0, 3.0, 4.0, 5.0, 6.0],
    opLevels: [0.96, 0.88, 0.82, 0.75, 0.65, 0.58],
    params: { formantShift: 0.58, vowelMorphX: 0.45, vowelMorphY: 0.75, waveFold: 0.70, modAttack: 0.08, modDecay: 0.72, carAttack: 0.06, carDecay: 0.68, chorusDelayMix: 0.40, spaceReverbMix: 0.38 },
  }),

  // ===========================================================================
  // 8. IMPORTED .SYX BANKS — 8 Classic 1983 ROM1A Voices + 4 User Slots
  // ===========================================================================
  {
    id: 'syx-rom1a-epiano1',
    name: '1. ROM1A #11 E.PIANO 1',
    category: 'IMPORTED_SYX',
    engineBadge: '6-OP FM',
    featureBadge: '.SYX CARTRIDGE',
    tags: ['SYX', 'Classic', '1983', 'Keys'],
    favorite: true,
    isSyx: true,
    targetEngineId: 0,
    opRatios: [1.0, 14.0, 1.0, 1.0, 1.0, 1.0],
    opLevels: [0.99, 0.42, 0.95, 0.58, 0.85, 0.48],
    params: { algMorph: 0.12, waveFold: 0.08, fmFeedback: 0.35, formantShift: 0.64, modAttack: 0.02, modDecay: 0.54, carAttack: 0.02, carDecay: 0.68, chorusDelayMix: 0.32, spaceReverbMix: 0.25 },
  },
  {
    id: 'syx-rom1a-bass1',
    name: '2. ROM1A #15 BASS 1',
    category: 'IMPORTED_SYX',
    engineBadge: '6-OP FM',
    featureBadge: '.SYX CARTRIDGE',
    tags: ['SYX', 'Bass', '1983', 'Slap'],
    favorite: true,
    isSyx: true,
    targetEngineId: 0,
    opRatios: [0.5, 1.0, 2.0, 3.0, 1.0, 4.0],
    opLevels: [0.99, 0.82, 0.76, 0.54, 0.88, 0.40],
    params: { algMorph: 0.16, waveFold: 0.24, fmFeedback: 0.55, formantShift: 0.56, modAttack: 0.01, modDecay: 0.25, carAttack: 0.01, carDecay: 0.30, chorusDelayMix: 0.10, spaceReverbMix: 0.08 },
  },
  {
    id: 'syx-rom1a-brass1',
    name: '3. ROM1A #01 BRASS 1',
    category: 'IMPORTED_SYX',
    engineBadge: '6-OP FM',
    featureBadge: '.SYX CARTRIDGE',
    tags: ['SYX', 'Brass', '1983'],
    favorite: false,
    isSyx: true,
    targetEngineId: 0,
    opRatios: [1.0, 1.0, 1.0, 2.0, 3.0, 1.0],
    opLevels: [0.98, 0.84, 0.86, 0.72, 0.55, 0.48],
    params: { algMorph: 0.42, waveFold: 0.48, fmFeedback: 0.52, formantShift: 0.62, modAttack: 0.16, modDecay: 0.70, carAttack: 0.10, carDecay: 0.62, chorusDelayMix: 0.34, spaceReverbMix: 0.30 },
  },
  {
    id: 'syx-rom1a-strings1',
    name: '4. ROM1A #04 STRINGS 1',
    category: 'IMPORTED_SYX',
    engineBadge: '6-OP FM',
    featureBadge: '.SYX CARTRIDGE',
    tags: ['SYX', 'Strings', '1983'],
    favorite: false,
    isSyx: true,
    targetEngineId: 0,
    opRatios: [1.0, 2.0, 1.0, 3.0, 2.0, 4.0],
    opLevels: [0.94, 0.72, 0.88, 0.64, 0.50, 0.42],
    params: { algMorph: 0.55, waveFold: 0.36, fmFeedback: 0.44, formantShift: 0.58, modAttack: 0.40, modDecay: 0.82, carAttack: 0.36, carDecay: 0.84, chorusDelayMix: 0.46, spaceReverbMix: 0.48 },
  },
  {
    id: 'syx-rom1a-marimba',
    name: '5. ROM1A #17 MARIMBA',
    category: 'IMPORTED_SYX',
    engineBadge: '6-OP FM',
    featureBadge: '.SYX CARTRIDGE',
    tags: ['SYX', 'Marimba', 'Mallet'],
    favorite: false,
    isSyx: true,
    targetEngineId: 0,
    opRatios: [1.0, 4.0, 1.0, 10.0, 2.0, 6.0],
    opLevels: [0.98, 0.68, 0.84, 0.42, 0.30, 0.22],
    params: { algMorph: 0.15, waveFold: 0.16, fmFeedback: 0.22, formantShift: 0.60, modAttack: 0.01, modDecay: 0.22, carAttack: 0.01, carDecay: 0.34, chorusDelayMix: 0.22, spaceReverbMix: 0.28 },
  },
  {
    id: 'syx-rom1a-tubebells',
    name: '6. ROM1A #26 TUB ER BELLS',
    category: 'IMPORTED_SYX',
    engineBadge: '6-OP FM',
    featureBadge: '.SYX CARTRIDGE',
    tags: ['SYX', 'Bells', '1983'],
    favorite: true,
    isSyx: true,
    targetEngineId: 0,
    opRatios: [1.0, 3.5, 1.0, 5.4, 2.0, 8.9],
    opLevels: [0.96, 0.76, 0.86, 0.62, 0.48, 0.38],
    params: { algMorph: 0.28, waveFold: 0.14, fmFeedback: 0.34, formantShift: 0.72, modAttack: 0.01, modDecay: 0.68, carAttack: 0.01, carDecay: 0.84, chorusDelayMix: 0.34, spaceReverbMix: 0.48 },
  },
  {
    id: 'syx-rom1a-clav1',
    name: '7. ROM1A #19 CLAV 1',
    category: 'IMPORTED_SYX',
    engineBadge: '6-OP FM',
    featureBadge: '.SYX CARTRIDGE',
    tags: ['SYX', 'Clav', 'Funk'],
    favorite: false,
    isSyx: true,
    targetEngineId: 0,
    opRatios: [1.0, 3.0, 2.0, 4.0, 6.0, 8.0],
    opLevels: [0.98, 0.82, 0.76, 0.64, 0.48, 0.36],
    params: { algMorph: 0.18, waveFold: 0.62, fmFeedback: 0.58, formantShift: 0.70, modAttack: 0.01, modDecay: 0.30, carAttack: 0.01, carDecay: 0.32, chorusDelayMix: 0.20, spaceReverbMix: 0.16 },
  },
  {
    id: 'syx-rom1a-synlead1',
    name: '8. ROM1A #22 SYN-LEAD 1',
    category: 'IMPORTED_SYX',
    engineBadge: '6-OP FM',
    featureBadge: '.SYX CARTRIDGE',
    tags: ['SYX', 'Lead', 'Solo'],
    favorite: false,
    isSyx: true,
    targetEngineId: 0,
    opRatios: [1.0, 2.0, 1.0, 3.0, 4.0, 2.0],
    opLevels: [0.96, 0.80, 0.84, 0.68, 0.52, 0.45],
    params: { algMorph: 0.46, waveFold: 0.70, fmFeedback: 0.54, formantShift: 0.68, modAttack: 0.04, modDecay: 0.62, carAttack: 0.02, carDecay: 0.54, chorusDelayMix: 0.36, spaceReverbMix: 0.30 },
  },
  ...createCategoryUserSlots('IMPORTED_SYX', {
    engineBadge: 'USER CUSTOM',
    featureBadge: 'USER SLOT',
    tags: ['User', 'SYX', 'Custom'],
    favorite: false,
    isSyx: true,
    targetEngineId: 0,
    opRatios: [1.0, 2.0, 1.0, 3.0, 4.0, 1.0],
    opLevels: [0.95, 0.65, 0.85, 0.55, 0.45, 0.35],
    params: { algMorph: 0.30, waveFold: 0.25, fmFeedback: 0.35, formantShift: 0.62, modAttack: 0.03, modDecay: 0.58, carAttack: 0.02, carDecay: 0.62, chorusDelayMix: 0.30, spaceReverbMix: 0.28 },
  }),
];

const USER_PRESETS_STORAGE_KEY = 'pocketgrid_user_bank_presets_v1';

export function loadPresetsWithUserSlots(): SynthPreset[] {
  try {
    const raw = localStorage.getItem(USER_PRESETS_STORAGE_KEY);
    if (!raw) return [...FACTORY_PRESETS];
    const savedUserMap = JSON.parse(raw) as Record<string, SynthPreset>;
    return FACTORY_PRESETS.map((p) => (savedUserMap[p.id] ? { ...p, ...savedUserMap[p.id] } : p));
  } catch {
    return [...FACTORY_PRESETS];
  }
}

export function saveUserPresetSlotToStorage(updatedPreset: SynthPreset): void {
  try {
    const raw = localStorage.getItem(USER_PRESETS_STORAGE_KEY);
    const savedUserMap: Record<string, SynthPreset> = raw ? JSON.parse(raw) : {};
    savedUserMap[updatedPreset.id] = updatedPreset;
    localStorage.setItem(USER_PRESETS_STORAGE_KEY, JSON.stringify(savedUserMap));
  } catch {
    // Ignore storage quota errors
  }
}

// 24-Scale Quantizer Definitions (Semitone offsets from Root)
export interface ScaleDefinition {
  id: string;
  name: string;
  intervals: number[];
  chords: { label: string; offsets: number[] }[];
}

export const MUSICAL_SCALES: ScaleDefinition[] = [
  {
    id: 'DORIAN',
    name: 'DORIAN',
    intervals: [0, 2, 3, 5, 7, 9, 10],
    chords: [
      { label: 'Dm9', offsets: [0, 7, 10, 14, 15] },
      { label: 'G13', offsets: [5, 11, 14, 16, 21] },
      { label: 'Cmaj9', offsets: [-2, 5, 9, 12, 14] },
      { label: 'Am11', offsets: [7, 12, 17, 22] },
      { label: 'Fmaj9', offsets: [3, 10, 14, 15, 19] },
      { label: 'Em11', offsets: [2, 7, 10, 14, 17] },
      { label: 'Bb13', offsets: [8, 14, 17, 20, 24] },
      { label: 'A7#9', offsets: [7, 11, 14, 17, 22] },
    ],
  },
  {
    id: 'MINOR_PENT',
    name: 'MIN PENT',
    intervals: [0, 3, 5, 7, 10],
    chords: [
      { label: 'Dm7', offsets: [0, 7, 10, 15] },
      { label: 'Fmaj7', offsets: [3, 10, 14, 19] },
      { label: 'Gm9', offsets: [5, 12, 15, 19, 20] },
      { label: 'Cadd9', offsets: [-2, 5, 10, 12, 14] },
      { label: 'Am7', offsets: [7, 12, 15, 19] },
      { label: 'Bbmaj7', offsets: [8, 15, 19, 22] },
      { label: 'Dm11', offsets: [0, 5, 10, 14, 17] },
      { label: 'A7sus', offsets: [7, 12, 14, 19] },
    ],
  },
  {
    id: 'MAJOR',
    name: 'MAJOR',
    intervals: [0, 2, 4, 5, 7, 9, 11],
    chords: [
      { label: 'Cmaj9', offsets: [0, 7, 11, 14, 16] },
      { label: 'Am9', offsets: [-3, 4, 7, 11, 12] },
      { label: 'Fmaj7#11', offsets: [5, 12, 16, 19, 23] },
      { label: 'G13sus', offsets: [7, 14, 17, 19, 21] },
      { label: 'Dm9', offsets: [2, 9, 12, 16, 17] },
      { label: 'Em7', offsets: [4, 9, 11, 14] },
      { label: 'G13', offsets: [7, 14, 17, 19, 21] },
      { label: 'Cadd9', offsets: [0, 7, 12, 14] },
    ],
  },
  {
    id: 'NATURAL_MINOR',
    name: 'AEOLIAN',
    intervals: [0, 2, 3, 5, 7, 8, 10],
    chords: [
      { label: 'Cm9', offsets: [0, 7, 10, 14, 15] },
      { label: 'Abmaj9', offsets: [8, 15, 19, 22, 24] },
      { label: 'Fm11', offsets: [5, 12, 15, 19, 22] },
      { label: 'G7alt', offsets: [7, 11, 15, 17, 20] },
      { label: 'Ebmaj9', offsets: [3, 10, 14, 17, 19] },
      { label: 'Gm7', offsets: [7, 10, 14, 17] },
      { label: 'Bb13', offsets: [10, 16, 19, 22, 26] },
      { label: 'Ddim7', offsets: [2, 5, 8, 11] },
    ],
  },
  {
    id: 'JAPANESE_INSEN',
    name: 'INSEN',
    intervals: [0, 1, 5, 7, 10],
    chords: [
      { label: 'Dsus4', offsets: [0, 5, 7, 12, 17] },
      { label: 'Ebmaj7', offsets: [1, 8, 12, 17] },
      { label: 'G7sus', offsets: [5, 10, 12, 17] },
      { label: 'Csus2', offsets: [-2, 5, 10, 12] },
      { label: 'D5', offsets: [0, 7, 12, 19] },
      { label: 'Ebadd9', offsets: [1, 8, 10, 15] },
      { label: 'Gm7', offsets: [5, 10, 12, 17] },
      { label: 'Dm7(b5)', offsets: [0, 3, 6, 10] },
    ],
  },
  {
    id: 'ARABIC_HIJAZ',
    name: 'HIJAZ',
    intervals: [0, 1, 4, 5, 7, 8, 10],
    chords: [
      { label: 'D7(b9)', offsets: [0, 4, 7, 10, 13] },
      { label: 'Ebmaj7', offsets: [1, 8, 12, 16] },
      { label: 'Gm(b6)', offsets: [5, 12, 15, 16] },
      { label: 'Cm7', offsets: [-2, 5, 8, 13] },
      { label: 'F#dim7', offsets: [4, 7, 10, 13] },
      { label: 'A7alt', offsets: [7, 11, 13, 16] },
      { label: 'Bbmaj7', offsets: [8, 12, 15, 19] },
      { label: 'Dmaj', offsets: [0, 4, 7, 12] },
    ],
  },
  {
    id: 'CHROMATIC',
    name: 'CHROMATIC',
    intervals: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
    chords: [
      { label: 'Dm9', offsets: [0, 7, 10, 14, 15] },
      { label: 'G13', offsets: [5, 11, 14, 16, 21] },
      { label: 'Cmaj9', offsets: [-2, 5, 9, 12, 14] },
      { label: 'Am11', offsets: [7, 12, 17, 22] },
      { label: 'Fmaj9', offsets: [3, 10, 14, 15, 19] },
      { label: 'Em9', offsets: [2, 7, 10, 14, 17] },
      { label: 'Bb13', offsets: [8, 14, 17, 20, 24] },
      { label: 'A7alt', offsets: [7, 11, 13, 15, 20] },
    ],
  },
];

export function quantizeNoteToScale(midiNote: number, rootSemitone: number, scale: ScaleDefinition): number {
  const rel = ((midiNote - rootSemitone) % 12 + 12) % 12;
  const octaveBase = midiNote - rel;
  let bestInterval = scale.intervals[0];
  let minDiff = 99;
  for (const interval of scale.intervals) {
    const diff = Math.abs(interval - rel);
    if (diff < minDiff) {
      minDiff = diff;
      bestInterval = interval;
    }
  }
  return octaveBase + bestInterval;
}
